import { useEffect, useId, useRef, useState } from "react";
import type { FieldOption } from "@/lib/field-util";

export interface SearchableSelectProps {
  value: string | null | undefined;
  onChange: (next: string) => void;
  options: readonly FieldOption[];
  placeholder: string;
  /**
   * Blendet das Suchfeld aus. Für kurze, feste Kataloge (sechs Kategorien), wo
   * Tippen langsamer ist als Lesen - die Optik bleibt dieselbe, nur die
   * Filterzeile fehlt. Default true, alle bestehenden Aufrufer bleiben gleich.
   */
  searchable?: boolean;
  /** Nur nötig, solange `searchable` an ist. */
  searchPlaceholder?: string;
  searchAriaLabel?: string;
  noResultsLabel?: string;
  emptyOptionLabel?: string;
  className?: string;
  editable?: boolean;
  variant?: "box" | "plain";
  disabled?: boolean;
  required?: boolean;
  id?: string;
  ariaLabel?: string;
}

/**
 * Reusable single-select primitive for longer catalogues.
 *
 * `FieldOption.section` creates visible groups while preserving the supplied
 * order. Domain-specific ordering and labels stay in thin wrappers.
 */
export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder,
  searchable = true,
  searchPlaceholder = "",
  searchAriaLabel = "",
  noResultsLabel = "",
  emptyOptionLabel,
  className = "",
  editable = true,
  variant = "box",
  disabled = false,
  required = false,
  id,
  ariaLabel,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const generatedListId = useId();
  const listId = `${id ?? generatedListId}-options`;
  const selected = options.find((option) => option.value === value);

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && event.target instanceof Node && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  if (!editable) {
    return (
      <span className={`smycles-field-value text-ink ${className}`}>
        {selected ? selected.label : <span className="text-ink-muted">{placeholder}</span>}
      </span>
    );
  }

  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visible = normalizedQuery
    ? options.filter((option) => option.label.toLocaleLowerCase().includes(normalizedQuery))
    : options;
  const sections = new Map<string, FieldOption[]>();
  for (const option of visible) {
    const section = option.section ?? "";
    const entries = sections.get(section);
    if (entries) entries.push(option);
    else sections.set(section, [option]);
  }

  const close = () => {
    setOpen(false);
    setQuery("");
  };
  const pick = (next: string) => {
    onChange(next);
    close();
  };
  const boxClass =
    variant === "plain"
      ? `smycles-field-value flex w-full min-w-0 items-center justify-between gap-2 rounded bg-transparent pr-0 text-left focus:outline-none focus:ring-1 focus:ring-ink-ui disabled:opacity-50 ${
          selected ? "font-medium text-ink" : "text-ink-muted"
        }`
      : "smycles-field-value flex w-full min-w-0 items-center justify-between gap-2 rounded bg-surface px-3 py-2.5 text-left text-ink ring-1 ring-ink/10 focus:outline-none focus:ring-1 focus:ring-ink-accent-ui disabled:opacity-50";

  return (
    <div ref={rootRef} className={`relative min-w-0 ${className}`}>
      <button
        id={id}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        disabled={disabled}
        data-value={value ?? ""}
        onClick={() => {
          setOpen((current) => !current);
          if (open) setQuery("");
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") close();
          if (event.key === "ArrowDown" && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className={boxClass}
      >
        <span className="truncate">{selected?.label ?? placeholder}</span>
        <Chevron open={open} />
      </button>

      {open ? (
        <div
          id={listId}
          role="listbox"
          className="absolute left-0 z-[70] mt-1 max-h-80 w-96 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-md bg-surface py-1 shadow-xl ring-1 ring-ink/15"
        >
          {searchable ? (
            <div className="sticky top-0 z-10 bg-surface px-2 pb-2 pt-1.5">
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") close();
                }}
                aria-label={searchAriaLabel}
                placeholder={searchPlaceholder}
                className="smycles-field-value w-full rounded bg-page px-2.5 py-2 text-ink ring-1 ring-ink/10 placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-ink-accent-ui"
              />
            </div>
          ) : null}

          {!required && emptyOptionLabel && !normalizedQuery ? (
            <SearchableSelectOption
              id={id && `${id}-option-`}
              label={emptyOptionLabel}
              selected={!value}
              muted
              onClick={() => pick("")}
            />
          ) : null}

          {visible.length === 0 ? (
            <div className="px-3 py-3 text-smycles-meta text-ink-muted">{noResultsLabel}</div>
          ) : (
            [...sections.entries()].map(([section, entries]) => (
              <div key={section || "__none__"}>
                {section ? (
                  <div className="sticky top-[49px] z-[5] bg-surface px-3 pb-1 pt-2 text-smycles-micro font-semibold uppercase tracking-[0.18em] text-ink-muted">
                    {section}
                  </div>
                ) : null}
                {entries.map((option) => (
                  <SearchableSelectOption
                    key={option.value}
                    id={id && `${id}-option-${option.value}`}
                    label={option.label}
                    selected={option.value === value}
                    onClick={() => pick(option.value)}
                  />
                ))}
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

function SearchableSelectOption({
  id,
  label,
  selected,
  muted = false,
  onClick,
}: {
  // Testanker; die Kennung trägt den WERT der Option, nicht ihre Beschriftung.
  id?: string;
  label: string;
  selected: boolean;
  muted?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      id={id}
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onClick}
      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-smycles-meta hover:bg-ink/[0.06] ${
        muted ? "text-ink-muted" : "text-ink"
      }`}
    >
      <span className="w-3 shrink-0 stroke-accent-decoration" aria-hidden="true">
        {selected ? "✓" : ""}
      </span>
      {/* data-label: der reine Eintragstext, ohne das Häkchen davor. Tests lesen
          ihn, statt sich auf „das letzte span" zu verlassen. */}
      <span className="truncate" data-label={label}>{label}</span>
    </button>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={`h-3 w-3 shrink-0 text-ink-muted transition-transform ${open ? "rotate-180" : ""}`}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}
